import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import { coreTelecomApiUrlPrefix, designFtoApiUrlPrefix } from '@telecom/shared/domain-config';
import type {
    DesignFtoApi,
    DesignFtoSearchConditions,
    DesignFtoBatchTaskResponse,
    StageResultDetail,
    WorkflowFlowStatusResponse,
    FlowStatusSSEMessage,
    ShareTaskResponse,
    TaskExistsResponse,
    ExportReportResponse,
    PatentBatchImagesResponse,
    PatentIdImageId,
    PatentImage,
    DesignFtoHistoryListRequest,
    DesignFtoHistoryListResponse,
    RecognizeMultiViewRequest,
    MultiViewRecognitionResult,
} from '../design-fto-api';
import type { PatentMessage } from '../../flow/stages/image_search/shared';
import type { ILocTreeItem } from '../../types';
import { GeneralFileUploadApi, type GetDownloadUrlRequest, type UploadedFileInfo } from '@telecom/shared/domain/file-upload';
import { isUserLogin } from '@telecom/shared/startup/user-auth.ts';

const useLocalDesignFtoApi = !!import.meta.env.VITE_USE_LOCAL_DESIGN_FTO_API;
const localFtoProApiUrlPrefix = 'http://127.0.0.1:8000/fto-image';

interface ExportReportEnvelope {
    success?: boolean;
    message?: string;
    data?: unknown;
}

const isExportReportEnvelope = (value: unknown): value is ExportReportEnvelope => {
    return !!value && typeof value === 'object' && 'data' in value;
};

const getExportReportBody = (result: unknown) => {
    if (!isExportReportEnvelope(result)) {
        return result;
    }
    if (result.success !== undefined) {
        return result;
    }
    return result.data;
};

const getExportReportPayload = (body: unknown) => {
    if (!isExportReportEnvelope(body)) {
        return body;
    }
    return body.data && typeof body.data === 'object' ? body.data : body;
};

const normalizeExportReportResponse = (result: unknown): ExportReportResponse => {
    const body = getExportReportBody(result);
    const payload = getExportReportPayload(body);
    const bodyRecord = body as ExportReportEnvelope | undefined;
    const payloadRecord = payload as ExportReportResponse | undefined;
    return {
        ...payloadRecord,
        success: bodyRecord?.success ?? payloadRecord?.success,
        message: payloadRecord?.message ?? bodyRecord?.message,
    };
};

const normalizeExportStatusUrl = (statusUrl: string) => {
    if (/^https?:\/\//.test(statusUrl)) {
        return statusUrl;
    }
    return statusUrl
        .replace(/^\/fto-image-api\/fto-image/, '')
        .replace(/^\/fto-image/, '');
};

/**
 * 外观FTO API 服务实现类
 * @description 实现所有 API 接口的真实调用
 */
@injectable()
export class DesignFtoApiServiceImpl extends PsHttpService implements DesignFtoApi {
    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;
    @inject(GeneralFileUploadApi)
    private _generalFileUploadApi!: GeneralFileUploadApi;

    /**
     * HTTP 服务配置选项
     */
    get options() {
        return {
            baseURL: useLocalDesignFtoApi ? localFtoProApiUrlPrefix : designFtoApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    /**
     * 提交批量任务
     * @param images - 图片列表（1-100个）
     */
    async submitBatchTask(images: DesignFtoSearchConditions[]): Promise<DesignFtoBatchTaskResponse> {
        const result = await this.post<PatSnapSuccessResponseData<any>>({
            url: '/batch/submit',
            data: {
                images,
            },
        });

        return result.data as DesignFtoBatchTaskResponse;
    }

    /**
     * 识别多张图片是否属于同一产品的多视角，并推荐主图/辅助图/排除图
     * @param request - 识别请求（图片列表、模型策略、语言）
     */
    async recognizeMultiView(request: RecognizeMultiViewRequest): Promise<MultiViewRecognitionResult> {
        const result = await this.post<PatSnapSuccessResponseData<MultiViewRecognitionResult>>({
            url: '/workflow/multi-view/recognize',
            data: request,
        });

        return result?.data as unknown as MultiViewRecognitionResult;
    }

    /**
     * 上传文件，file 和 url 必填一个
     * @param file - 文件对象
     * @param url - 文件 URL
     */
    async uploadFile(file: File | string): Promise<UploadedFileInfo> {
        let url: string = '';
        let fileObj: File | undefined = undefined;

        if (file instanceof File) {
            fileObj = file;
        } else {
            url = file;
        }
        const res = await this._generalFileUploadApi.uploadSingleFile({
            file: fileObj,
            url,
            folder_path: 'eureka-ip/user-upload',
            file_prefix: 'design_fto_user_upload',
            keep_original_name: false,
        });
        return res?.uploaded_files[0];
    }

    /**
     * 获取文件签名地址
     * @param request 请求参数
     * @returns 签名后的URL
     */
    async signFile(request: GetDownloadUrlRequest): Promise<string> {
        return this._generalFileUploadApi.getDownloadUrl(request);
    }

    /**
     * 获取LOC分类树节点数据
     * @param code - LOC分类代码，为空则获取根节点
     */
    async getLOCItemsData(code?: string): Promise<ILocTreeItem[]> {
        const result = await this.get<PatSnapSuccessResponseData<ILocTreeItem[]>>({
            url: '/workflow/classification/helper-section-subclass',
            cache: true,
            params: {
                type: 'loc',
                code,
            },
        });
        return result?.data as unknown as ILocTreeItem[];
    }

    /**
     * 根据关键词搜索LOC分类树
     * @param keyword - 搜索关键词
     */
    async getLOCTreeByKeyword(keyword: string): Promise<{
        query_results: Array<{
            cls_tree: ILocTreeItem;
        }>;
    }> {
        const result = await this.get<PatSnapSuccessResponseData<{
            query_results: Array<{
                cls_tree: ILocTreeItem;
            }>;
        }>>({
            url: '/workflow/classification/helper-search',
            params: {
                type: 'loc',
                keyword,
            },
        });
        return result?.data as unknown as {
            query_results: Array<{
                cls_tree: ILocTreeItem;
            }>;
        };
    }

    /**
     * 根据图片预测LOC分类
     * @param params - 预测参数
     */
    async predictLOC(params: { url?: string; description?: string }): Promise<ILocTreeItem[]> {
        const result = await this.post<PatSnapSuccessResponseData<ILocTreeItem[]>>({
            url: '/workflow/loc-predict',
            data: params,
        });
        return (result?.data || []) as unknown as ILocTreeItem[];
    }

    async getStreamBaseUrl() {
        const login = await isUserLogin();
        if (login) {
            return this.options.baseURL;
        }
        return this.options.baseURL?.replace('/fto-image-api/', '/public-share-image/');
    }

    /**
     * 获取工作流流程状态（SSE流式接口）
     * @param taskId - 任务ID
     * @returns PsHttpSSE实例，用于监听事件和控制流
     */
    async getFlowStatus(taskId: string): Promise<PsHttpSSE<FlowStatusSSEMessage>> {
        const baseUrl = await this.getStreamBaseUrl();
        return this._factorySsePsHttp<FlowStatusSSEMessage>()
            .setFetchHeaders(this.options.headers)
            .init({
                method: 'GET',
                url: `${baseUrl}/workflow/${taskId}/flow-status`,
                hasFinalMessage: () => true,
            });
    }

    /**
     * 获取Stage执行结果
     * @param taskId - 任务ID
     * @param stage - Stage名称
     * @param version - 版本，默认为 latest（最新）
     */
    async getStageResult(taskId: string, stage: string, version: string = 'latest'): Promise<StageResultDetail> {
        const result = await this.get<PatSnapSuccessResponseData<{
            success: boolean;
            data: StageResultDetail;
            message: string;
        }>>({
            url: `/workflow/${taskId}/stage/${stage}/result`,
            params: {
                version,
            },
        });

        return result?.data as unknown as StageResultDetail;
    }

    /**
     * 获取工作流流程状态快照（非流式，一次性返回所有数据）
     * @param taskId - 任务ID
     */
    async getFlowStatusSnapshot(taskId: string): Promise<WorkflowFlowStatusResponse> {
        const result = await this.get<PatSnapSuccessResponseData<{
            success: boolean;
            data: WorkflowFlowStatusResponse;
            message: string;
        }>>({
            url: `/workflow/${taskId}/flow-status-snapshot`,
        });

        return result?.data as unknown as WorkflowFlowStatusResponse;
    }

    /**
     * 分享任务（设置为公开状态）
     * @param taskId - 任务ID
     * @description 分享后任何人都可以查看任务状态和结果，只有任务创建者可以执行此操作
     */
    async shareTask(taskId: string): Promise<ShareTaskResponse> {
        const result = await this.post<PatSnapSuccessResponseData<{
            success: boolean;
            data: ShareTaskResponse;
            message: string;
        }>>({
            url: `/workflow/${taskId}/share`,
        });

        return result?.data as unknown as ShareTaskResponse;
    }

    /**
     * 检查任务是否存在
     * @param taskId - 任务ID
     * @description 用于在调用其他接口前验证任务ID的有效性
     */
    async checkTaskExists(taskId: string): Promise<TaskExistsResponse> {
        const result = await this.request<PatSnapSuccessResponseData<{
            success: boolean;
            data: TaskExistsResponse;
            message: string;
        }>>({
            method: 'GET',
            url: `/workflow/${taskId}/exists`,
        });

        return result?.data as unknown as TaskExistsResponse;
    }

    /**
     * 导出报告
     * @param taskId - 任务ID
     */
    async exportReport(taskId: string): Promise<ExportReportResponse> {
        const result = await this.get<PatSnapSuccessResponseData<ExportReportResponse>>({
            url: `/workflow/${taskId}/export`,
        });

        return normalizeExportReportResponse(result);
    }

    async getExportReportStatus(statusUrl: string): Promise<ExportReportResponse> {
        const result = await this.get<PatSnapSuccessResponseData<ExportReportResponse>>({
            url: normalizeExportStatusUrl(statusUrl),
        });

        return normalizeExportReportResponse(result);
    }

    async patentBatchImagesByImageId(patent_id_image_ids: PatentIdImageId[]): Promise<PatentBatchImagesResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentBatchImagesResponse>>({
            baseURL: coreTelecomApiUrlPrefix,
            url: '/patent/image-id/batch-image-url-map',
            cache: true,
            data: {
                patent_id_image_ids,
            },
        });

        return res?.data as unknown as PatentBatchImagesResponse;
    }

    async editAndRetry(taskId: string, stage: string, editedState: Record<string, any>): Promise<{ success: boolean; message: string }> {
        const result = await this.post<PatSnapSuccessResponseData<{ success: boolean; message: string }>>({
            url: `/workflow/${taskId}/stage/${stage}/edit-and-retry`,
            data: {
                edited_state: editedState,
                resume_from_current_stage: true,
            },
        });

        return result?.data as unknown as { success: boolean; message: string };
    }

    async patentSearch(q: string) {
        const result = await this.post<PatSnapSuccessResponseData<{ data: { patent_info?: Record<string, any> } }>>({
            url: '/workflow/patent-search',
            data: { q },
        });

        const info = result?.data as any;
        if (!info?.patent_id) return null;

        return {
            patent_id: info.patent_id,
            patent_pn: info.pn,
            authority: info.authority,
            title: info.title || '',
            simple_legal_status: info.simple_legal_status,
            pdf_image: (info.pdf_image || []) as PatentImage[],
        };
    }

    /**
     * 获取外观FTO 历史任务记录列表
     * @param params - 分页参数。首次加载不传，接口返回最新 3 条；
     *                继续加载时传入上一轮返回的 next_offset，默认每页 15 条。
     */
    async getHistoryList(params: DesignFtoHistoryListRequest = {}): Promise<DesignFtoHistoryListResponse> {
        const queryParams: Record<string, number | string> = {};
        if (params.offset !== undefined) queryParams.offset = params.offset;
        if (params.limit !== undefined) queryParams.limit = params.limit;
        if (params.source) queryParams.source = params.source;

        const result = await this.get<PatSnapSuccessResponseData<DesignFtoHistoryListResponse>>({
            url: '/workflow/history',
            params: queryParams,
        });

        return result?.data as unknown as DesignFtoHistoryListResponse;
    }
}
